<template>
	<v-autocomplete
		:model-value="modelValue.value"
		:autofocus="autoFocus()"
		class="pr-5"
		:custom-filter="searchCompositeKeys"
		density="compact"
		:disabled="readOnly || mode === Mode.BLANK_TABLE_CELL"
		hide-details="auto"
		:items="filterCompositeKeys(modelValue)"
		item-title="name"
		item-value="key"
		:label="keyPickerLabel()"
		:menu-props="{
			offsetY: true,
			contentClass: 'composite-key-selector-list-menu',
			contentProps: {
				onKeydown: (event) => {
					event.stopPropagation();
				},
			},
		}"
		single-line
		@update:model-value="updateValue"
	>
		<template #item="{ item, props }">
			<v-list-item
				v-bind="props"
				class="composite-key-selector-title-container"
			>
				<template #title>
					<div class="composite-key-selector-list-item">
						<span class="composite-key-selector-key">
							{{ item.title }}
						</span>
						<div
							v-if="item.raw.study"
							class="composite-key-selector-path-container"
						>
							<span class="composite-key-selector-path">
								{{ item.raw.study }}
							</span>
							<span
								v-if="item.raw.studyPart"
								class="composite-key-selector-path"
							>
								{{ item.raw.studyPart }}
							</span>
							<span
								v-if="item.raw.studyArm"
								class="composite-key-selector-path"
							>
								{{ item.raw.studyArm }}
							</span>
							<span
								v-if="item.raw.population"
								class="composite-key-selector-path"
							>
								{{ item.raw.population }}
							</span>
							<span
								v-if="item.raw.subgroup"
								class="composite-key-selector-path"
							>
								{{ item.raw.subgroup }}
							</span>
						</div>
					</div>
				</template>
			</v-list-item>
		</template>
	</v-autocomplete>
</template>

<script>
import { Mode } from "./VMetadataConstants.js";

export default {
	name: "VMetadataBranchValueField",

	inject: ["autoFocus"],

	props: {
		compositeKeyUtils: { type: Object, default: null },
		extractionCompositeKeys: { type: Array, default: () => [] },
		mode: { type: String, default: Mode.STANDARD },
		modelValue: { type: Object, default: null },
		readOnly: { type: Boolean, default: false },
	},

	emits: ["update:modelValue"],

	setup() {},

	data() {
		return {
			Mode,
		};
	},
	computed: {},

	methods: {
		filterCompositeKeys(metadatum) {
			const nonEmptyKeys = this.extractionCompositeKeys.filter(
				(key) => key.study,
			);
			if (this.mode === Mode.BRANCH_SELECT) {
				return nonEmptyKeys.map((key, index) =>
					this.getCompositeKeyDisplayData(key, metadatum, index),
				);
			}

			let filteredKeys = [];
			const tagName = this.compositeKeyUtils.isReferenceBranchTag(
				metadatum.tag.name,
			)
				? this.compositeKeyUtils.referenceBranchTag
				: metadatum.tag.name;
			switch (tagName) {
				case this.compositeKeyUtils.studyTag:
					filteredKeys = nonEmptyKeys.filter(
						(key) => key.study && !key.studyPart && !key.studyArm,
					);
					break;
				case this.compositeKeyUtils.studyPartTag:
					filteredKeys = nonEmptyKeys.filter(
						(key) => key.study && key.studyPart && !key.studyArm,
					);
					break;
				case this.compositeKeyUtils.studyArmTag:
					filteredKeys = nonEmptyKeys.filter(
						(key) =>
							key.study && key.studyArm && !key.population && !key.subgroup,
					);
					break;
				case this.compositeKeyUtils.populationTag:
					filteredKeys = nonEmptyKeys.filter(
						(key) =>
							key.study && key.studyArm && key.population && !key.subgroup,
					);
					break;
				case this.compositeKeyUtils.subgroupTag:
					filteredKeys = nonEmptyKeys.filter(
						(key) => key.study && key.studyArm && key.subgroup,
					);
					break;
				case this.compositeKeyUtils.referenceBranchTag:
					filteredKeys = nonEmptyKeys;
					break;
			}

			return filteredKeys.map((key, index) =>
				this.getCompositeKeyDisplayData(key, metadatum, index),
			);
		},
		getCompositeKeyDisplayData(item, metadatum, index) {
			let name = "";
			let study = "";
			let studyPart = "";
			let studyArm = "";
			let population = "";

			const rawTagName = metadatum.tag ? metadatum.tag.name : "";
			// Normalize legacy "Reference study arm abbreviation" so old saves
			// hit the same case as canonical "Reference study branch".
			const tagName = this.compositeKeyUtils.isReferenceBranchTag(rawTagName)
				? this.compositeKeyUtils.referenceBranchTag
				: rawTagName;
			switch (tagName) {
				case this.compositeKeyUtils.studyTag:
					name = item.study;
					break;
				case this.compositeKeyUtils.studyPartTag:
					name = item.studyPart;
					study = item.study;
					break;
				case this.compositeKeyUtils.studyArmTag:
					name = item.studyArm;
					study = item.study;
					studyPart = item.studyPart;
					break;
				case this.compositeKeyUtils.populationTag:
					name = item.population;
					study = item.study;
					studyPart = item.studyPart;
					studyArm = item.studyArm;
					break;
				case this.compositeKeyUtils.subgroupTag:
					name = item.subgroup;
					study = item.study;
					studyPart = item.studyPart;
					studyArm = item.studyArm;
					population = item.population;
					break;
				case this.compositeKeyUtils.referenceBranchTag:
					if (item.subgroup) {
						name = item.subgroup;
						study = item.study;
						studyPart = item.studyPart;
						studyArm = item.studyArm;
						population = item.population;
					} else if (item.population) {
						name = item.population;
						study = item.study;
						studyPart = item.studyPart;
						studyArm = item.studyArm;
					} else if (item.studyArm) {
						name = item.studyArm;
						study = item.study;
						studyPart = item.studyPart;
					} else if (item.studyPart) {
						name = item.studyPart;
						study = item.study;
					} else {
						name = item.study;
					}
					break;
				default:
					if (item.subgroup) {
						name = item.subgroup;
						study = item.study;
						studyPart = item.studyPart;
						studyArm = item.studyArm;
						population = item.population;
					} else if (item.population) {
						name = item.population;
						study = item.study;
						studyPart = item.studyPart;
						studyArm = item.studyArm;
					} else if (item.studyArm) {
						name = item.studyArm;
						study = item.study;
						studyPart = item.studyPart;
					} else if (item.studyPart) {
						name = item.studyPart;
						study = item.study;
					} else {
						name = item.study;
					}
					break;
			}
			return {
				index,
				key: item.key,
				name,
				study,
				studyPart,
				studyArm,
				population,
			};
		},
		keyPickerLabel() {
			if (this.mode === Mode.BRANCH_SELECT) {
				return "Study branch";
			}

			let label;
			const rawTagName = this.modelValue?.tag?.name;
			const tagName = this.compositeKeyUtils.isReferenceBranchTag(rawTagName)
				? this.compositeKeyUtils.referenceBranchTag
				: rawTagName;
			switch (tagName) {
				case this.compositeKeyUtils.studyTag:
					label = "Study";
					break;
				case this.compositeKeyUtils.studyPartTag:
					label = "Part";
					break;
				case this.compositeKeyUtils.studyArmTag:
					label = "Arm";
					break;
				case this.compositeKeyUtils.populationTag:
					label = "Population";
					break;
				case this.compositeKeyUtils.subgroupTag:
					label = "Subgroup";
					break;
				case this.compositeKeyUtils.referenceBranchTag:
					label = "Reference";
					break;
			}

			return label;
		},
		searchCompositeKeys(item, queryText) {
			const key = item.toLowerCase();
			const searchText = queryText.toLowerCase();
			return key.indexOf(searchText) > -1;
		},
		updateValue(value) {
			this.$emit("update:modelValue", {
				...this.modelValue,
				value,
			});
		},
	},
};
</script>

<style lang="scss">
.composite-key-selector-key {
	font-size: 14px;
}

.composite-key-selector-list-item {
	display: flex;
	align-items: center;
}

.composite-key-selector-list-menu .v-select-list {
	padding: 0px;
}

.composite-key-selector-list-menu .v-list-item:not(:last-child) {
	border-bottom: 1px dotted #000;
}

.composite-key-selector-path {
	margin-left: 10px;
	color: gray;
	font-size: 12px;
	font-style: italic;
}

.composite-key-selector-path-container {
	display: flex;
	flex-direction: column;
	gap: 3px;
}

.composite-key-selector-title-container {
	width: 100%;
}
</style>
